import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

// Create the context
const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(undefined);

export type BreadCrumb = { title: string; link: string };
type BreadcrumbsContextType = {
  breadCrumbs: BreadCrumb[];
  updateBreadCrumbs: (links: BreadCrumb[]) => void;
};

export const BreadcrumbsProvider = ({ children }: { children: React.ReactNode }) => {
  const [breadCrumbs, setBreadCrumbs] = useState<BreadCrumb[]>([]);

  const updateBreadCrumbs = useCallback((links: BreadCrumb[]) => {
    // Set breadcrumbs based on the page, or empty array if page not found
    setBreadCrumbs(links);
  }, []);

  // Context value object - memoize to prevent unnecessary re-renders
  const contextValue = useMemo<BreadcrumbsContextType>(() => ({
    breadCrumbs,
    updateBreadCrumbs,
  }), [breadCrumbs, updateBreadCrumbs]);

  return <BreadcrumbsContext.Provider value={contextValue}>{children}</BreadcrumbsContext.Provider>;
};

// Custom hook to use the breadcrumbs context
export const useBreadcrumbsContext = () => {
  const context = useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error("useBreadcrumbsContext must be used within a BreadcrumbsProvider");
  }
  return context;
};

// Alternative: Export context directly if you prefer useContext(BreadcrumbsContext)
export { BreadcrumbsContext };
